import { logger } from "@/lib/logger";
import { routeAfterSignIn } from "./route-after-sign-in";
import type { AppRouter, SupabaseBrowserClient } from "./types";

interface CreateSignInHandlerArgs {
  supabase: SupabaseBrowserClient;
  router: AppRouter;
  email: string;
  password: string;
  redirectAfterLogin?: string | null;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
}

export function createSignInHandler(args: CreateSignInHandlerArgs) {
  const {
    supabase,
    router,
    email,
    password,
    redirectAfterLogin,
    setIsLoading,
    setError,
    setSuccess,
  } = args;

  return async (e: React.FormEvent, userType: "employee" | "client") => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        logger.error("Sign in error", error);

        // Handle specific error cases
        if (error.message.includes("Email not confirmed")) {
          setError(
            "Please verify your email address before signing in. Check your inbox for a verification link."
          );
        } else if (error.message.includes("Invalid login credentials")) {
          setError("Invalid email or password. Please check your credentials and try again.");
        } else {
          setError(error.message);
        }
        return;
      }

      if (data.user) {
        await routeAfterSignIn({
          supabase,
          router,
          user: data.user,
          userType,
          redirectAfterLogin,
          setError,
        });
      }
    } catch (err) {
      logger.error("Unexpected error", err);
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };
}
